/**
 * Error severity levels for categorization and filtering
 */
export enum ErrorSeverity {
  LOW = 'low',           // Minor issues, user can continue
  MEDIUM = 'medium',     // Significant issues, some features unavailable
  HIGH = 'high',         // Critical issues, app mostly unusable
  CRITICAL = 'critical'  // Fatal issues, app cannot function
}

/**
 * Error categories for routing to appropriate handlers
 */
export enum ErrorCategory {
  HTTP = 'http',                   // Network/API errors
  CLIENT = 'client',               // JavaScript errors
  FIREBASE = 'firebase',           // Firebase SDK errors
  ASYNC = 'async',                 // Promise rejections
  UNKNOWN = 'unknown'              // Unclassified errors
}

/**
 * Structured error interface for logging and processing
 */
export interface AppError {
  message: string;                 // User-friendly message
  originalError: Error | unknown;  // Original error object
  category: ErrorCategory;
  severity: ErrorSeverity;
  context: {
    userId?: string;               // Current user ID (if authenticated)
    route?: string;                // Current route path
    timestamp: number;             // Unix timestamp
    appVersion: string;            // From environment
    userAgent: string;             // Browser info
    additionalData?: Record<string, unknown>; // Extra context
  };
  stackTrace?: string;             // Full stack trace (dev only)
}

/**
 * User-facing error messages mapped to error types
 */
export const USER_ERROR_MESSAGES = {
  [ErrorCategory.HTTP]: {
    401: 'Your session has expired. Please log in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    500: 'An unexpected server error occurred. Please try again later.',
    503: 'The service is temporarily unavailable. Please try again in a few minutes.',
    default: 'A network error occurred. Please check your connection and try again.'
  },
  [ErrorCategory.CLIENT]: 'An unexpected error occurred. Our team has been notified.',
  [ErrorCategory.FIREBASE]: {
    'auth/user-not-found': 'Account not found. Please check your credentials.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'permission-denied': 'You do not have permission to access this data.',
    default: 'A service error occurred. Please try again.'
  },
  [ErrorCategory.ASYNC]: 'An operation failed to complete. Please try again.',
  [ErrorCategory.UNKNOWN]: 'An unexpected error occurred. Please try again or contact support.'
} as const;
